import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useGetBlogsQuery } from "../../redux/api/blogsApi";
import { useGetProductsQuery } from "../../redux/api/productsApi";
import { useDispatch, useSelector } from "react-redux";
import { setCartItem } from "../../redux/slices/cartSlice";
import { openCart } from "../../redux/slices/uiSlice";
import { FileText, ShoppingBag, SearchX } from "lucide-react";
import Loader from "../layout/Loader";
import ProductCard from "../product/ProductCard";
import toast from "react-hot-toast";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("keyword")?.trim() || "";

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);

  const { data: blogData, isLoading: blogsLoading } = useGetBlogsQuery(
    { keyword, page: 1 },
    { skip: !keyword }
  );

  const { data: productData, isLoading: productsLoading } = useGetProductsQuery(
    { keyword, page: 1 },
    { skip: !keyword }
  );

  const blogs = blogData?.blogs ?? [];
  const products = productData?.products ?? [];
  const isLoading = blogsLoading || productsLoading;
  const hasResults = blogs.length > 0 || products.length > 0;

  // Same addItemToCart logic used across WishlistPage, PreviouslyViewed, etc.
  const addItemToCart = (product) => {
    const existingCartItem = cartItems.find(
      (item) => item.product === product._id
    );

    if (existingCartItem && existingCartItem.quantity >= product.stock) {
      toast.error("Maximum items already added");
      return;
    }

    if (existingCartItem) {
      toast.error("Already in your bag");
      return;
    }

    dispatch(
      setCartItem({
        product: product._id,
        name: product.name,
        price: product.price,
        image: product.images[0]?.url,
        stock: product.stock,
        quantity: 1,
      })
    );
    dispatch(openCart());
    toast.success("Added to bag");
  };

  if (!keyword) {
    return (
      <div className="max-w-4xl mx-auto mt-32 md:mt-44 px-4 text-center">
        <p className="text-stone-500 font-sourcesans text-lg">
          Enter a search term to find products and articles.
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Search: {keyword} — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto mt-32 md:mt-44 px-4 sm:px-6 mb-20">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-playfair text-[#1c1917]">
            Results for{" "}
            <span className="text-[#BC3F4A]">&ldquo;{keyword}&rdquo;</span>
          </h1>
          {!isLoading && (
            <p className="mt-1 text-sm text-stone-500 font-sourcesans">
              {products.length + blogs.length}{" "}
              {products.length + blogs.length === 1 ? "result" : "results"} found
            </p>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : !hasResults ? (
          <div className="text-center py-20 flex flex-col items-center gap-3">
            <SearchX className="w-10 h-10 text-stone-300" aria-hidden="true" />
            <p className="text-stone-500 font-sourcesans text-lg">
              No results for &ldquo;{keyword}&rdquo;
            </p>
            <p className="text-stone-400 font-sourcesans text-sm">
              Try a different term or{" "}
              <Link to="/supportUkraine" className="text-[#BC3F4A] underline">
                browse the shop
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="space-y-12">

            {/* ── Products ─────────────────────────────────────────────────── */}
            {products.length > 0 && (
              <section aria-label="Product results">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-playfair text-lg text-[#1c1917] flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-[#BC3F4A]" aria-hidden="true" />
                    Products
                    <span className="text-sm font-sourcesans text-stone-400 font-normal">
                      ({products.length})
                    </span>
                  </h2>
                  {products.length > 6 && (
                    <Link
                      to={`/shop?keyword=${encodeURIComponent(keyword)}`}
                      className="text-xs text-[#BC3F4A] hover:underline font-sourcesans"
                    >
                      View all →
                    </Link>
                  )}
                </div>

                {/* Same grid as ShopAll / WishlistPage */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {products.slice(0, 8).map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      addItemToCart={addItemToCart}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* ── Articles ─────────────────────────────────────────────────── */}
            {blogs.length > 0 && (
              <section aria-label="Article results">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-playfair text-lg text-[#1c1917] flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#BC3F4A]" aria-hidden="true" />
                    Articles
                    <span className="text-sm font-sourcesans text-stone-400 font-normal">
                      ({blogs.length})
                    </span>
                  </h2>
                  {blogs.length > 5 && (
                    <Link
                      to={`/blogs?keyword=${encodeURIComponent(keyword)}`}
                      className="text-xs text-[#BC3F4A] hover:underline font-sourcesans"
                    >
                      View all →
                    </Link>
                  )}
                </div>

                <div className="flex flex-col divide-y divide-stone-200 border border-stone-200 rounded-xl overflow-hidden bg-white">
                  {blogs.slice(0, 5).map((blog) => {
                    const stripped =
                      blog.description?.replace(/<[^>]+>/g, "") || "";
                    const date = new Date(blog.createdAt).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    );

                    return (
                      <Link
                        key={blog._id}
                        to={`/blogs/${blog._id}`}
                        className="flex gap-4 p-4 hover:bg-[#f9f8f4] transition-colors duration-150"
                      >
                        {blog.images?.[0]?.url && (
                          <img
                            src={blog.images[0].url}
                            alt={blog.name}
                            className="w-16 h-16 object-cover rounded-md shrink-0 bg-stone-100"
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <div className="min-w-0 flex flex-col justify-center">
                          <p className="font-playfair text-sm md:text-base text-[#1c1917] line-clamp-1">
                            {blog.name}
                          </p>
                          <p className="text-xs text-stone-500 font-sourcesans mt-0.5 line-clamp-2 leading-relaxed">
                            {stripped.slice(0, 120)}
                            {stripped.length > 120 && "…"}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {blog.category && (
                              <span className="text-xs text-[#BC3F4A] bg-[#BC3F4A]/10 px-2 py-0.5 rounded-full">
                                {blog.category}
                              </span>
                            )}
                            <time
                              className="text-xs text-stone-400"
                              dateTime={blog.createdAt}
                            >
                              {date}
                            </time>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

          </div>
        )}
      </div>
    </>
  );
};

export default SearchResults;